/**
 * Per-tab module-level registry of tool-call id → latest progress message
 * (PLT-5031).
 *
 * Some backend tools (today: `get_company_physical_climate_risk`) run for
 * minutes inside a single call and emit coarse progress as `status` SSE events
 * keyed by `toolCallId` (see the chat handler). The chat adapter writes each
 * message here; the tool trail reads it for the step that is running, to show a
 * live "what's happening now" line instead of a frozen spinner (PLT-7912). The
 * entry is cleared when the matching `tool-result` event lands.
 *
 * Same pattern (and rationale) as `visibility-registry.ts`: a module-level Map
 * plus a listener set, consumed from React through `useSyncExternalStore`. A
 * module store (not React context) is used because the value is produced deep
 * in the adapter's streaming loop and consumed in an assistant-ui tool UI that
 * only receives `{ args, result, toolCallId }` — there is no prop channel for
 * a custom per-part field, so a lookup-by-id store is the clean bridge.
 */

const progressByToolCall = new Map<string, string>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Record (or replace) the latest progress message for a tool call. */
export function setToolProgress(toolCallId: string, message: string): void {
  if (!toolCallId) return;
  if (progressByToolCall.get(toolCallId) === message) return; // no-op: avoid a needless re-render
  progressByToolCall.set(toolCallId, message);
  notify();
}

/**
 * Drop a tool call's progress once its result has arrived (or the run ended).
 * Safe to call for an id that was never recorded.
 */
export function clearToolProgress(toolCallId: string): void {
  if (progressByToolCall.delete(toolCallId)) notify();
}

export function getToolProgress(toolCallId: string): string | undefined {
  return progressByToolCall.get(toolCallId);
}

export function subscribeToolProgress(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only helper. Production code should not rely on the registry being empty. */
export function clearAllToolProgress(): void {
  progressByToolCall.clear();
  notify();
}
